import { useEffect, useMemo, useRef } from "react";
import { useDataChannel, useRoomContext } from "@livekit/components-react";
import { RoomEvent, type RemoteParticipant } from "livekit-client";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { videoService } from "../api/video.api";
import {
  ADMISSION_TIMEOUT_MS,
  AdmissionTracker,
  type Admission,
  type AdmissionEvent,
} from "../lib/admissionTracker";
import { PARTICIPANT_ADMITTED_TYPE, parseRoomDataMessage } from "../lib/roomDataMessages";

const JOINING_TOAST_ID = "call-admission-joining";
const JOINED_VISIBLE_MS = 4_000;
const failedToastId = (identity: string) => `call-admission-failed-${identity}`;
const lateJoinedToastId = (identity: string) => `call-admission-late-joined-${identity}`;

interface AdmissionNoticesProps {
  roomId: string;
  liveKitToken: string;
  /** Strict room owner (room.is_owner). A link guest is never the owner. */
  isOwner: boolean;
}

/**
 * "X is joining…" notices after a waiting participant is admitted (#1916).
 *
 * Everyone in the call when the backend's participantAdmitted message arrives
 * sees one combined notice until the admitted people connect. After
 * ADMISSION_TIMEOUT_MS, the owner — and whoever admitted them — is told
 * "X couldn't connect" with a button that puts X back in the waiting list;
 * everyone else's notice just clears. People who join the call later never
 * got the message, so they see nothing.
 *
 * Renders nothing itself; must be mounted inside a <LiveKitRoom> provider.
 */
export function AdmissionNotices({ roomId, liveKitToken, isOwner }: AdmissionNoticesProps) {
  const { t } = useTranslation();
  const room = useRoomContext();
  const decoder = useMemo(() => new TextDecoder(), []);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  // Latest props for the tracker's recipient rule and the toast actions,
  // without rebuilding the tracker (and losing its state) on every render.
  const latest = useRef({ roomId, liveKitToken, isOwner, t });
  latest.current = { roomId, liveKitToken, isOwner, t };

  const tracker = useMemo(
    () =>
      new AdmissionTracker(
        (a) => latest.current.isOwner || a.admittedBy === room.localParticipant.identity,
      ),
    [room],
  );

  const render = useRef<(events: AdmissionEvent[]) => void>(() => {});
  render.current = (events) => {
    const { t: tr } = latest.current;
    for (const event of events) {
      switch (event.kind) {
        case "joining":
          toast.loading(
            event.names.length === 1
              ? tr("call.admission.joining", { name: event.names[0] })
              : tr("call.admission.joiningMany", { count: event.names.length }),
            { id: JOINING_TOAST_ID, duration: Infinity },
          );
          break;
        case "batch-ended":
          if (event.joined.length === 0) {
            toast.dismiss(JOINING_TOAST_ID);
          } else {
            toast.success(
              event.joined.length === 1
                ? tr("call.admission.joined", { name: event.joined[0] })
                : tr("call.admission.joinedMany", { count: event.joined.length }),
              { id: JOINING_TOAST_ID, duration: JOINED_VISIBLE_MS },
            );
          }
          break;
        case "failed":
          showFailed(event.admission);
          break;
        case "late-joined":
          // A fresh toast, not an update of the failure one: sonner merges an
          // update into the old toast, so its put-back action would survive.
          toast.dismiss(failedToastId(event.admission.identity));
          toast.success(tr("call.admission.joined", { name: event.admission.username }), {
            id: lateJoinedToastId(event.admission.identity),
            duration: JOINED_VISIBLE_MS,
          });
          break;
      }
    }
  };

  const showFailed = (admission: Admission) => {
    const { t: tr } = latest.current;
    const id = failedToastId(admission.identity);
    toast.error(tr("call.admission.failed", { name: admission.username }), {
      id,
      duration: Infinity,
      action: {
        label: tr("call.admission.putBack"),
        onClick: () => {
          const { roomId: rid, liveKitToken: token } = latest.current;
          videoService
            .requeueParticipant(rid, admission.participantId, token)
            .then(() => {
              tracker.forget(admission.identity);
              toast.dismiss(id);
            })
            .catch((err: unknown) => {
              console.error("AdmissionNotices: requeue failed:", err);
              toast.error(
                latest.current.t("call.admission.putBackError", { name: admission.username }),
              );
            });
        },
      },
    });
  };

  useDataChannel((msg) => {
    const data = parseRoomDataMessage(decoder, msg.payload);
    if (data?.type !== PARTICIPANT_ADMITTED_TYPE) return;
    if (typeof data.identity !== "string" || typeof data.participant_id !== "string") return;
    const admission: Admission = {
      participantId: data.participant_id,
      identity: data.identity,
      username: typeof data.username === "string" ? data.username : "",
      admittedBy: typeof data.admitted_by === "string" ? data.admitted_by : null,
    };

    // A previous failure notice for the same person is superseded.
    toast.dismiss(failedToastId(admission.identity));
    clearTimeout(timers.current.get(admission.identity));
    render.current(tracker.admit(admission));
    timers.current.set(
      admission.identity,
      setTimeout(() => {
        timers.current.delete(admission.identity);
        render.current(tracker.expire(admission.identity));
      }, ADMISSION_TIMEOUT_MS),
    );
    // Already here (the message raced the connection): resolve at once.
    if (room.remoteParticipants.has(admission.identity)) {
      clearTimeout(timers.current.get(admission.identity));
      timers.current.delete(admission.identity);
      render.current(tracker.arrive(admission.identity));
    }
  });

  useEffect(() => {
    const onConnected = (participant: RemoteParticipant) => {
      clearTimeout(timers.current.get(participant.identity));
      timers.current.delete(participant.identity);
      render.current(tracker.arrive(participant.identity));
    };
    room.on(RoomEvent.ParticipantConnected, onConnected);
    return () => {
      room.off(RoomEvent.ParticipantConnected, onConnected);
    };
  }, [room, tracker]);

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
      toast.dismiss(JOINING_TOAST_ID);
    };
  }, []);

  return null;
}
